import { type AnimationProps, motion } from 'motion/react';
import type { ReactNode } from 'react';

import { atoms } from '../../css/atoms/atoms.css';
import { Box, type BoxProps } from '../Box/Box';
import { DialogCloseButton } from '../Dialog/DialogCloseButton';

// Append additional semantic tones here
type BannerTone = 'info';

// Map Banner tone to a Box background
const bannerToneToBoxBackgroundMap: { [key in BannerTone]: BoxProps['background'] } = {
  info: 'infoLight',
};

interface BannerProps {
  tone?: BannerTone;
  children: ReactNode;
  onClose?: () => void;
}

const framerMotionVariants: AnimationProps['variants'] = {
  hidden: { height: 0, opacity: 0, overflow: 'hidden' },
  visible: { height: 'auto', opacity: 1, overflow: 'visible' },
};

export function Banner({ onClose, children, tone = 'info' }: BannerProps): JSX.Element {
  return (
    <motion.div
      className={atoms({
        overflow: 'hidden',
        display: 'flex',
        gap: 'large',
        justifyContent: 'spaceBetween',
        paddingX: {
          mobile: 'small',
          tablet: 'medium',
          desktop: 'large',
        },
        paddingY: 'medium',
        background: bannerToneToBoxBackgroundMap[tone],
      })}
      initial="hidden"
      animate="visible"
      variants={framerMotionVariants}
      transition={{ duration: 0.5, ease: 'easeInOut' }}
    >
      <Box
        display="flex"
        justifyContent={{ desktop: 'center', tablet: 'flexStart', mobile: 'flexStart' }}
        flexGrow={1}
      >
        {children}
      </Box>
      {onClose && <DialogCloseButton onClose={onClose} />}
    </motion.div>
  );
}
